import React, { useState } from 'react';
import { Faq } from '../components/Faq';
import { Ledger } from '../components/Ledger';
import { faqsContact } from '../data/faqsContact';
import { CONTACT_EMAIL, DEMO_URL, FORM_ENDPOINT, SIGN_IN_URL } from '../data/links';
const VERTICALS = ['Pharma', 'Finance', 'Insurance', 'Legal', 'Other'];
const OWNERSHIP = [{
  n: '01',
  label: 'Your library',
  body: 'It starts from a governed baseline — regulatory, brand, role — and becomes proprietary the first time your best reviewer’s correction gets atomized. Generic goes in once. What comes back is yours, and it never returns to the marketplace.'
}, {
  n: '02',
  label: 'Your environment',
  body: 'The library lives where your data lives. Your policies, your transcripts, your corrections stay inside your walls. Nothing leaves. Nothing trains anyone else’s model.'
}, {
  n: '03',
  label: 'Your leverage',
  body: 'Instruction data is portable: readable by your compliance officer, compilable for any model. Switch models Tuesday. The library doesn’t notice.'
}];
const NEXT = [{
  n: '01',
  title: 'We compile your workflow.',
  body: 'Your obligations, seated and fired, on the request you named.'
}, {
  n: '02',
  title: 'You see the receipt.',
  body: 'Which rules rode, which versions, on what trigger — signed.'
}, {
  n: '03',
  title: 'A 30-minute walkthrough.',
  body: 'Where it fits, and where it doesn’t. If it’s not for you, we’ll say so.'
}];
export function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [vertical, setVertical] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'mail'>('idle');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const fields = {
      name,
      email,
      vertical: vertical ?? '—',
      workflow,
      // Identifies the sender in a busy inbox rather than every submission
      // arriving under one identical subject line.
      _subject: `Synthia contact — ${trimmedName || 'no name'}${vertical ? ` (${vertical})` : ''}`
    };

    // No endpoint configured yet: hand off to the visitor's mail client so the
    // message still has somewhere to go.
    if (!FORM_ENDPOINT) {
      const body = [`Name: ${name}`, `Email: ${email}`, `Vertical: ${fields.vertical}`, '', `Workflow: ${workflow}`].join('\n');
      setStatus('mail');
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(fields._subject)}&body=${encodeURIComponent(body)}`;
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(fields)
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('sent');
      setName('');
      setEmail('');
      setVertical(null);
      setWorkflow('');
    } catch {
      setStatus('error');
    }
  };
  return <>
      <header className="hero contact-hero" data-id="element-375" data-mp="2hia472u71kwo">
        {/* Full-width hero: no .col-l, whose divider would double the frame's
            own right rule. */}
        <div style={{
        width: '100%'
      }} data-id="element-376" data-mp="3a4uvk3ao5x08">
          <div className="lab-row" data-id="element-377" data-mp="3a5xki5oflpdi">
            <span className="clabel" data-id="element-378" data-mp="3f3z5wx9by0s5">Talk to us</span>
          </div>
          <div className="head-block" data-id="element-379" data-mp="3f24tk7c4jgzo">
            <h1 style={{
            maxWidth: 'none',
            fontSize: 'clamp(26px,3.7vw,46px)'
          }} data-id="element-380" data-mp="314zhnqpqbnwi">
              Bring your <span className="g" data-id="element-381" data-mp="355rtmrpzk6wh">highest standards.</span>
            </h1>
          </div>
          <div className="sub-block" data-id="element-382" data-mp="1ppj6enwv3o5b">
            <p className="sub" data-id="element-383" data-mp="1pohukh80uxmj">
              The first thing we build is your instruction library &mdash; your written rules and the ones nobody
              wrote down, atomized into binary DO/DON&rsquo;Ts: owned, versioned, triggered.
            </p>

            <form className="form" onSubmit={submit} data-id="element-384" data-mp="257as6xpphix3">
              <div className="field" data-id="element-385" data-mp="255px96zhbxyp">
                <label htmlFor="cf-name" data-id="element-386" data-mp="2087kt6m970ly">Name</label>
                <input id="cf-name" type="text" required value={name} onChange={e => setName(e.target.value)} data-id="element-387" data-mp="208zicmzev8ev" />
              </div>
              <div className="field" data-id="element-388" data-mp="1v7a3ew92iali">
                <label htmlFor="cf-email" data-id="element-389" data-mp="2sx5339qgsxep">Work email</label>
                <input id="cf-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-id="element-390" data-mp="2suiwpdfb8ny2" />
              </div>
              <div className="field" data-id="element-391" data-mp="2exvlq6qivx95">
                <span className="cf-label" id="cf-vertical-label" data-id="element-392" data-mp="2ewud08wzbmul">
                  Vertical
                </span>
                <div className="chips" role="group" aria-labelledby="cf-vertical-label" data-id="element-393" data-mp="17h7g0tg3lst8">
                  {VERTICALS.map(v => <button type="button" key={v} className={`chip${vertical === v ? ' sel' : ''}`} aria-pressed={vertical === v} onClick={() => setVertical(v)} data-id="element-394" data-mp="3f5ty64jnabcl">
                      {v}
                    </button>)}
                </div>
              </div>
              <div className="field" data-id="element-395" data-mp="1r7828d5dt0n">
                <label htmlFor="cf-workflow" data-id="element-396" data-mp="1q4c3m0yx0dv">Tell us about your most challenging workflow</label>
                <textarea id="cf-workflow" required value={workflow} onChange={e => setWorkflow(e.target.value)} placeholder="I have to respond to compliance findings, and every draft comes back with something missing…" data-id="element-397" data-mp="6nnumy4p7zbm" />
              </div>
              <div data-id="element-398" data-mp="2hh0em9y4f17z">
                <button className="btn g" type="submit" disabled={status === 'sending'} data-id="element-399" data-mp="2hilbw7btky9j">
                  {status === 'sending' ? 'Sending…' : 'Send message →'}
                </button>
              </div>
              {/* aria-live region: one node that stays mounted, so each outcome is
                  announced rather than silently swapping the form's tail. */}
              <p className="cf-sent" role="status" aria-live="polite" data-id="element-400" data-mp="3ouayd5a2ff47">
                {status === 'sent' ? <span className="cf-ok" data-id="element-401" data-mp="7l4e0tg4thma">
                    Message sent. We read every one &mdash; expect a reply within a business day.
                  </span> : null}
                {status === 'error' ? <span className="cf-err" data-id="element-402" data-mp="gdpv85447g90">
                    That didn&rsquo;t go through. Write to{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} data-id="element-403" data-mp="cdfnamwrifri">{CONTACT_EMAIL}</a> and we&rsquo;ll pick it up there.
                  </span> : null}
                {status === 'mail' ? <>
                    Your message is queued in your mail app. If nothing opened, write to{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} data-id="element-404" data-mp="14x9okcjp35nc">{CONTACT_EMAIL}</a> directly.
                  </> : null}
              </p>
            </form>
          </div>
        </div>
      </header>

      <div className="split" id="demo" data-id="element-405" data-mp="v3asnva7si3u">
        <div className="sp-l" data-id="element-406" data-mp="zyzslzq5c10o">
          <span className="clabel" data-id="element-407" data-mp="zy6jwt7mn1fw">Book a demo</span>
          <h2 data-id="element-408" data-mp="m835goezoc10">Or skip the form.</h2>
          <p className="lede" data-id="element-409" data-mp="m71sgeeexfgn">
            Thirty minutes on a workflow you actually care about. We compile it, you read the receipt,
            and if it isn&rsquo;t a fit we&rsquo;ll tell you.
          </p>
          <div className="ctas" style={{
          marginTop: 26
        }} data-id="element-410" data-mp="cc9sj3xcygip">
            <a className="btn g" href={DEMO_URL} target="_blank" rel="noopener" data-id="element-411" data-mp="h8qje5vxz8zh">
              Schedule a demo &rarr;
            </a>
          </div>
        </div>
        <div className="sp-r" data-id="element-412" data-mp="7bccoxztlykd">
          <div style={{
          width: '100%'
        }} data-id="element-413" data-mp="7blrdpff0jar">
            <div className="reach" data-id="element-414" data-mp="7akdzeaxfkpv">
              <div className="reachr" data-id="element-415" data-mp="nql5lj0uykhf">
                <span className="rl" data-id="element-416" data-mp="npjx9mb88c3e">Email us here</span>
                <a className="rv" href="mailto:hello@meetsynthia.ai" data-id="element-417" data-mp="is1ktly03eqn">
                  hello@meetsynthia.ai
                </a>
              </div>
              <div className="reachr" data-id="element-418" data-mp="dr41h6198enk">
                <span className="rl" data-id="element-419" data-mp="8tm1w694nbs8">Talk to sales</span>
                <a className="rv" href="mailto:sales@meetsynthia.ai" data-id="element-420" data-mp="8t2jkofkeldo">
                  sales@meetsynthia.ai
                </a>
              </div>
              <div className="reachr" data-id="element-421" data-mp="1k5cj26ifn8">
                <span className="rl" data-id="element-422" data-mp="ii7g7w22p5">Already a customer</span>
                <a className="rv" href={SIGN_IN_URL} target="_top" rel="noopener" data-id="element-423" data-mp="3r7bsd9kntc5t">
                  app.meetsynthia.ai
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid3" data-id="element-424" data-mp="3h9nkrj1yga68">
        {OWNERSHIP.map(o => <div className="cell" key={o.n} data-id="element-425" data-mp="3hb8jlkvayf9w">
            <div className="lab" data-id="element-426" data-mp="3d8vb4op6839f">
              <span data-id="element-427" data-mp="3d8lsxnadb0ea">{o.label}</span>
              <span className="n" data-id="element-428" data-mp="1sq1k687qp57f">{o.n}</span>
            </div>
            <p className="lede" data-id="element-429" data-mp="1sruin0878j4c">{o.body}</p>
          </div>)}
      </div>

      <div className="split" data-id="element-430" data-mp="1wps7g1y25377">
        <div className="sp-l" data-id="element-431" data-mp="1wt7r7i2of0ey">
          <p className="kicker" style={{
          marginTop: 0
        }} data-id="element-432" data-mp="21py7a5ehdw0d">
            Rent the model.
            <br data-id="element-433" data-mp="26pap2bqhwcw3" />
            Own the instructions.
          </p>
        </div>
        <div className="sp-r" data-id="element-434" data-mp="26n6n3zc5pnyg">
          <div style={{
          width: '100%'
        }} data-id="element-435" data-mp="192amap9at3d7">
            <span className="clabel" data-id="element-436" data-mp="193lr8sehch5w">What happens next</span>
            <div className="steps" style={{
            marginTop: 20
          }} data-id="element-437" data-mp="1e03u1mq925fl">
              {NEXT.map(s => <div className="stepr" key={s.n} data-id="element-438" data-mp="1izorfspru5q7">
                  <span className="sn" data-id="element-439" data-mp="1ixvl6ej2lniq">{s.n}</span>
                  <span className="st" data-id="element-440" data-mp="1nqydkjc42qxc">
                    <b data-id="element-441" data-mp="1nrzuv5jq09ni">{s.title}</b> {s.body}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <div className="stoprow" data-id="element-442" data-mp="v9cc3ws756pl">
        <h2 data-id="element-443" data-mp="v8jfhomybxl6">
          Asked and <span className="g" data-id="element-444" data-mp="oxm2th6me8vb">answered.</span>
        </h2>
        <span className="clabel" data-id="element-445" data-mp="ox2jbw1bncf6">FAQ</span>
      </div>

      <Faq items={faqsContact} data-id="element-446" data-mp="twf5sfoa6hh8" />

      <Ledger data-id="element-447" data-mp="tvdtk7vj3oxx" />
    </>;
}